import { ApolloError, useMutation } from '@apollo/client';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { TotemMark } from '@/shared/brand/TotemLogo';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';

import { setStoredRefresh, useCustomerAuthStore, type CustomerProfile } from './customerAuthStore';
import { CUSTOMER_LOGIN_MUTATION } from './portal.gql';

/* eslint-disable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment */

interface LoginPayload {
  access_token: string;
  refresh_token: string;
  customer: CustomerProfile;
}

export function PortalLoginPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setSession = useCustomerAuthStore((s) => s.setSession);
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [login, { loading }] = useMutation(CUSTOMER_LOGIN_MUTATION);

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    try {
      const res = await login({ variables: { input: { identifier: identifier.trim(), password } } });
      const payload = res.data?.customerLogin as LoginPayload | undefined;
      if (!payload) throw new Error('login_failed');
      setStoredRefresh(payload.refresh_token);
      setSession(payload.access_token, payload.customer);
      navigate('/portal/pets', { replace: true });
    } catch (err) {
      const raw = err instanceof ApolloError ? (err.graphQLErrors[0]?.message ?? err.message) : 'error';
      if (raw.includes('customer_portal_disabled')) setError(t('portal.error_disabled'));
      else if (raw.includes('invalid') || raw.includes('credentials')) setError(t('portal.error_credentials'));
      else setError(t('portal.error_generic'));
    }
  };

  return (
    <div className="bg-muted/30 flex min-h-screen items-center justify-center p-4">
      <form
        onSubmit={(e) => void onSubmit(e)}
        className="bg-card w-full max-w-sm space-y-4 rounded-lg border p-6 shadow-sm"
      >
        <div className="flex flex-col items-center space-y-2 text-center">
          <TotemMark className="text-primary h-12 w-auto" aria-hidden />
          <h1 className="font-display text-xl font-extrabold tracking-tight">{t('portal.app_name')}</h1>
          <p className="text-muted-foreground text-sm">{t('portal.login_subtitle')}</p>
        </div>
        {error ? (
          <div className="border-destructive/40 bg-destructive/5 text-destructive rounded-md border p-2 text-sm">
            {error}
          </div>
        ) : null}
        <div className="space-y-1">
          <Label>{t('portal.identifier')}</Label>
          <Input
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            autoComplete="username"
            placeholder={t('portal.identifier_ph')}
          />
        </div>
        <div className="space-y-1">
          <Label>{t('portal.password')}</Label>
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
          />
        </div>
        <Button type="submit" className="w-full" disabled={loading || !identifier.trim() || !password}>
          {t('portal.login_button')}
        </Button>
      </form>
    </div>
  );
}
